import { useState, useEffect, useCallback } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Resource } from "../../hooks/useResourceManagement";
import { safeResourceIdToString, logResourceMatching } from "../utils/resourceIdMapping";
import { Project, ProjectTask } from "../../types";
import { ResourceAssignment } from "../types";

export type { ResourceAssignment };

interface TaskAssignmentCache {
  [taskId: string]: {
    assignments: ResourceAssignment[];
    lastFetched: number;
  };
}

export const useResourceAssignmentOptimized = (selectedProject: Project | null) => {
  // Simplified state management to avoid React queue issues
  const [state, setState] = useState({
    resources: [] as Resource[],
    projectTasks: [] as ProjectTask[],
    selectedTaskId: null as string | null,
    assignmentCache: {} as TaskAssignmentCache,
    isLoading: false,
    isDialogOpen: false,
    editingAssignment: null as ResourceAssignment | null,
    isOrgChartOpen: false,
    isCreatingRecords: false,
    lastProcessedProjectId: null as string | null,
    processingLock: false,
    userRole: '' as string,
    canAddResource: true,
  });

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;

  // Memoized update function to prevent unnecessary re-renders
  const updateState = useCallback((updates: Partial<typeof state>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    if (selectedProject) {
      loadProjectTasks();
      fetchAvailableResources();
    }
  }, [selectedProject]);

  // Create project task records when Resource Assignment tab is loaded or when project changes
  useEffect(() => {
    console.log('🔍 ResourceAssignmentTab: Effect triggered - selectedProject?.id:', selectedProject?.id, 'lastProcessedProjectId:', state.lastProcessedProjectId, 'processingLock:', state.processingLock);
    
    if (selectedProject && 
        selectedProject.tasks && 
        selectedProject.tasks.length > 0 && 
        !state.isCreatingRecords &&
        !state.processingLock &&
        selectedProject.id !== state.lastProcessedProjectId) {
      
      console.log('🔄 ResourceAssignmentTab: Starting project task record creation for NEW project:', selectedProject.name, 'ID:', selectedProject.id);
      createProjectTaskRecords();
    }
  }, [selectedProject?.id, selectedProject?.tasks?.length, state.lastProcessedProjectId, state.processingLock]);

  const loadProjectTasks = () => {
    if (!selectedProject?.tasks) {
      updateState({ projectTasks: [] });
      return;
    }
    
    console.log('📋 PLAN IMPLEMENTATION: Loading project tasks from project.tasks JSON:', {
      projectId: selectedProject.id,
      totalTasks: selectedProject.tasks.length,
      sampleTasks: selectedProject.tasks.slice(0, 3).map(t => ({
        taskId: t.taskId || t.name,
        name: t.name,
        duration: t.duration
      }))
    });

    // Convert project tasks and generate unique task IDs
    const tasksWithIds = selectedProject.tasks.map((task, index) => ({
      ...task,
      taskId: task.taskId || task.name || `Task-${index + 1}`,
      id: task.id || `task-${index + 1}`
    }));

    const updates: Partial<typeof state> = { projectTasks: tasksWithIds };
    
    // Set the first task as selected if none is selected
    if (tasksWithIds.length > 0 && !state.selectedTaskId) {
      const firstTaskId = tasksWithIds[0].taskId || tasksWithIds[0].name;
      updates.selectedTaskId = firstTaskId;
    }

    updateState(updates);
  };

  const fetchAvailableResources = async () => {
    if (!selectedProject) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Determine caller role + permission so we can pick the right data path
      // and gate the Add Resource action for Project Managers.
      const { data: profile } = await supabase
        .from('users')
        .select('role, company_name')
        .eq('id', user.id)
        .maybeSingle();
      const role = (profile?.role || '').trim();
      const company = (profile?.company_name || '').trim();

      let canAdd = true;
      if (role === 'Manager' && company) {
        const { data: perm } = await supabase
          .from('project_access_permissions')
          .select('ra_addresource__project_manager')
          .eq('company_name', company)
          .maybeSingle();
        canAdd = perm ? (perm as any).ra_addresource__project_manager !== false : true;
      }

      let resources: Resource[] = [];
      if (role === 'Manager') {
        // Project Managers don't own the resource pool. Use the service-role
        // edge function to read the full project resource pool so they can
        // pick from any unassigned resource.
        const { data: fnData, error } = await supabase.functions.invoke(
          'get-project-resource-pool',
          { body: { project_name: selectedProject.name } }
        );
        if (error) throw error;
        resources = (fnData?.data as Resource[]) || [];
      } else {
        const { data, error } = await supabase
          .from('resources')
          .select('*')
          .eq('user_id', user.id)
          .eq('project_id', selectedProject.id);
        if (error) throw error;
        resources = (data || []) as Resource[];
      }
      
      console.log('📊 PLAN IMPLEMENTATION - Available resources fetched:', {
        role,
        canAdd,
        total: resources.length,
      });
      
      updateState({ resources, userRole: role, canAddResource: canAdd });
    } catch (error) {
      console.error('Error fetching resources:', error);
      toast.error('Failed to load available resources');
    }
  };

  const createProjectTaskRecords = async () => {
    if (!selectedProject || !selectedProject.tasks || selectedProject.tasks.length === 0) {
      console.log('⏭️ ResourceAssignmentTab: Skipping record creation - conditions not met');
      return;
    }

    // Check if already processing to prevent race conditions
    if (state.isCreatingRecords || state.processingLock) {
      console.log('⏭️ ResourceAssignmentTab: Skipping record creation - already in progress');
      return;
    }

    // Set flags to prevent multiple concurrent executions
    updateState({ 
      isCreatingRecords: true,
      processingLock: true,
      lastProcessedProjectId: selectedProject.id 
    });
    console.log('🚀 ResourceAssignmentTab: Setting isCreatingRecords flag to true for project:', selectedProject.id);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('❌ ResourceAssignmentTab: No authenticated user found');
        updateState({ isCreatingRecords: false, processingLock: false });
        return;
      }

      console.log('🔍 ResourceAssignmentTab: Checking existing project task records for project:', selectedProject.name);

      // Sort project tasks by start date to ensure proper task_id sequencing
      const sortedTasks = [...selectedProject.tasks].sort((a, b) => {
        const startA = a.startDate ? new Date(a.startDate) : new Date();
        const startB = b.startDate ? new Date(b.startDate) : new Date();
        return startA.getTime() - startB.getTime();
      });

      let successCount = 0;
      let skipCount = 0;
      
      // Process tasks individually to handle database constraint violations gracefully
      for (let i = 0; i < sortedTasks.length; i++) {
        const task = sortedTasks[i];
        const taskId = i + 1; // Incremental serial number starting from 1

        // Get resource assignments for this task by querying resource_assignment table directly
        console.log(`🔍 ResourceAssignmentTab: Fetching assignments for task: ${task.name}`);
        const { data: taskAssignments, error: assignmentsError } = await supabase
          .from('resource_assignment')
          .select('resource_id')
          .eq('project_id', selectedProject.id)
          .eq('task_id', task.name) // Use task.name to match task_id in resource_assignment
          .eq('user_id', user.id);

        if (assignmentsError) {
          console.error(`❌ ResourceAssignmentTab: Error fetching assignments for task ${task.name}:`, assignmentsError);
          // Continue with empty resources array if there's an error
        }

        // Extract unique resource IDs from assignments
        const uniqueResourceIds = [...new Set(
          (taskAssignments || [])
            .map(assignment => assignment.resource_id)
            .filter(resourceId => resourceId && resourceId.trim() !== '')
        )];

        // Extract dates and duration from task or first assignment
        let startDate = task.startDate || null;
        let endDate = task.endDate || null;
        let duration = task.duration || null;

        console.log(`📝 ResourceAssignmentTab: Preparing record for task: ${task.name}, resource_ids: [${uniqueResourceIds.join(', ')}]`);

        const taskRecord = {
          user_id: user.id,
          project_id: selectedProject.id,
          project_name: selectedProject.name,
          task_id: taskId.toString(),
          name: task.name,
          start_date: startDate,
          end_date: endDate,
          duration: duration,
          completion_percentage: 0,
          resources: uniqueResourceIds // Populate the resources JSONB field with unique resource IDs
        };

        try {
          console.log(`📥 ResourceAssignmentTab: Attempting to insert record for task: ${task.name}`);
          
          const { error } = await supabase
            .from('project_tasks')
            .insert([taskRecord]);

          if (error) {
            // Check if it's a duplicate constraint violation (error code 23505)
            if (error.code === '23505' || error.message.includes('duplicate key value violates unique constraint')) {
              console.log(`ℹ️ ResourceAssignmentTab: Task ${task.name} already exists (caught by database constraint), skipping`);
              skipCount++;
            } else {
              console.error(`❌ ResourceAssignmentTab: Error creating record for task ${task.name}:`, error);
            }
          } else {
            successCount++;
            console.log(`✅ ResourceAssignmentTab: Successfully created record for task: ${task.name} with resource IDs: [${taskRecord.resources.join(', ')}]`);
          }
        } catch (error: any) {
          // Handle any other database constraint violations gracefully
          if (error.code === '23505' || error.message?.includes('duplicate key value violates unique constraint')) {
            console.log(`ℹ️ ResourceAssignmentTab: Task ${task.name} already exists (caught by exception), skipping`);
            skipCount++;
          } else {
            console.error(`💥 ResourceAssignmentTab: Unexpected error creating record for task ${task.name}:`, error);
          }
        }
      }

      const totalProcessed = successCount + skipCount;
      console.log(`🎉 ResourceAssignmentTab: Processing complete for project: ${selectedProject.name} - Created: ${successCount}, Skipped: ${skipCount}, Total: ${totalProcessed}/${sortedTasks.length}`);
      
      if (successCount > 0) {
        toast.success(`Created ${successCount} new project task records for ${selectedProject.name}`);
      } else if (skipCount > 0) {
        console.log(`ℹ️ ResourceAssignmentTab: All ${skipCount} tasks already existed for project: ${selectedProject.name}`);
      }
    } catch (error) {
      console.error('💥 ResourceAssignmentTab: Error in createProjectTaskRecords:', error);
      toast.error('Failed to create project task records');
    } finally {
      // Always reset the flags
      console.log('🏁 ResourceAssignmentTab: Setting isCreatingRecords flag to false for project:', selectedProject?.id);
      updateState({ 
        isCreatingRecords: false,
        processingLock: false 
      });
    }
  };

  const fetchTaskAssignments = async (taskId: string, forceRefresh = false) => {
    if (!selectedProject || !taskId) return [];
    
    // Check cache first
    const cached = state.assignmentCache[taskId];
    const now = Date.now();
    
    if (!forceRefresh && cached && (now - cached.lastFetched) < CACHE_DURATION) {
      console.log('📈 PLAN OPTIMIZATION: Using cached assignments for task:', taskId);
      return cached.assignments;
    }
    
    updateState({ isLoading: true });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      console.log('🔄 Fetching assignments for task:', { taskId, projectId: selectedProject.id });

      // Determine caller role to pick the correct data path.
      const { data: profile } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();
      const role = (profile?.role || '').trim();

      let data: any[] = [];
      if (role === 'Manager' || role === 'Director') {
        // Project Managers and Project Directors share the same view of
        // assigned resources for every project task. Use the service-role
        // edge function so rows owned by other users (e.g. the counterpart
        // Manager/Director) are also returned.
        const { data: fnData, error } = await supabase.functions.invoke(
          'get-manager-resource-assignments',
          { body: { project_id: selectedProject.id, task_id: taskId } }
        );
        if (error) throw error;
        data = (fnData?.data as any[]) || [];
      } else {
        const { data: rows, error } = await supabase
          .from('resource_assignment')
          .select('*')
          .eq('project_id', selectedProject.id)
          .eq('task_id', taskId)
          .eq('user_id', user.id);
        if (error) throw error;
        data = rows || [];
      }

      const assignments: ResourceAssignment[] = data.map(assignment => ({
        ...assignment,
        email: assignment.email || null
      }));
      
      console.log('✅ PLAN SUCCESS: Task assignments fetched:', {
        taskId,
        assignmentCount: assignments.length,
        sampleAssignments: assignments.slice(0, 2).map(a => ({
          name: a.name,
          role_type: a.role_type,
          resource_id: safeResourceIdToString(a.resource_id),
          email: a.email
        }))
      });

      // Update cache
      updateState({ 
        assignmentCache: {
          ...state.assignmentCache,
          [taskId]: {
            assignments,
            lastFetched: now
          }
        }
      });

      return assignments;
    } catch (error) {
      console.error('Error fetching task assignments:', error);
      toast.error('Failed to load task assignments');
      return [];
    } finally {
      updateState({ isLoading: false });
    }
  };

  const updateProjectTaskResources = async (taskId: string, taskName: string) => {
    if (!selectedProject) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log('🔄 RESOURCES UPDATE: Updating project_tasks resources field for task:', {
        taskId,
        taskName,
        projectId: selectedProject.id
      });

      // Fetch current assignments for this task with resource_id
      const { data: currentAssignments, error: fetchError } = await supabase
        .from('resource_assignment')
        .select('resource_id')
        .eq('project_id', selectedProject.id)
        .eq('task_id', taskId)
        .eq('user_id', user.id);

      if (fetchError) {
        console.error('Error fetching current assignments for resources update:', fetchError);
        return;
      }

      // Extract unique resource IDs from current assignments
      const uniqueResourceIds = [...new Set(
        (currentAssignments || [])
          .map(assignment => assignment.resource_id)
          .filter(resourceId => resourceId && resourceId.trim() !== '')
      )];

      console.log('🆔 RESOURCES UPDATE: Extracted unique resource IDs:', uniqueResourceIds);

      // Update the project_tasks record with current resource IDs
      const { error: updateError } = await supabase
        .from('project_tasks')
        .update({ 
          resources: uniqueResourceIds // Update the resources JSONB field with current resource IDs
        })
        .eq('user_id', user.id)
        .eq('project_id', selectedProject.id)
        .eq('name', taskName);

      if (updateError) {
        console.error('Error updating project_tasks resources field:', updateError);
        return;
      }

      console.log('✅ RESOURCES UPDATE: Successfully updated project_tasks resources field:', {
        taskName,
        resourceCount: uniqueResourceIds.length,
        resourceIds: uniqueResourceIds
      });

    } catch (error) {
      console.error('Error in updateProjectTaskResources:', error);
    }
  };

  const handleDeleteResource = async (assignmentId: string) => {
    try {
      // First get the assignment details before deletion for updating project_tasks.
      // For Managers, use the service-role edge function so RLS doesn't hide rows
      // owned by other users (e.g. the Project Director).
      let taskIdForUpdate: string | null = null;
      if (state.userRole === 'Manager' || state.userRole === 'Director') {
        const { data: fnData } = await supabase.functions.invoke(
          'get-manager-resource-assignments',
          { body: { project_id: selectedProject?.id, task_id: state.selectedTaskId } }
        );
        const rows = (fnData?.data as any[]) || [];
        taskIdForUpdate = rows.find((r) => r.id === assignmentId)?.task_id ?? null;
      } else {
        const { data: assignmentToDelete } = await supabase
          .from('resource_assignment')
          .select('task_id')
          .eq('id', assignmentId)
          .single();
        taskIdForUpdate = assignmentToDelete?.task_id ?? null;
      }

      if (state.userRole === 'Manager' || state.userRole === 'Director') {
        const { data, error } = await supabase.functions.invoke(
          'manage-manager-resource-assignment',
          { body: { action: 'delete', id: assignmentId } }
        );
        if (error) throw error;
        if ((data as any)?.error) throw new Error((data as any).error);
      } else {
        const { error } = await supabase
          .from('resource_assignment')
          .delete()
          .eq('id', assignmentId);
        if (error) throw error;
      }

      toast.success('Resource assignment deleted successfully');
      
      // Clear cache for current task and immediately fetch fresh data
      if (state.selectedTaskId) {
        const updatedCache = { ...state.assignmentCache };
        delete updatedCache[state.selectedTaskId];
        updateState({ assignmentCache: updatedCache });
        
        await fetchTaskAssignments(state.selectedTaskId, true);
        
        if (taskIdForUpdate) {
          await updateProjectTaskResources(taskIdForUpdate, taskIdForUpdate);
        }
      }
    } catch (error) {
      console.error('Error deleting resource assignment:', error);
      toast.error('Failed to delete resource assignment');
    }
  };

  const handleSaveResource = async (formData: any) => {
    if (!selectedProject || !formData.resource_id) {
      toast.error('Please select a resource');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Find the selected resource using the UUID from formData
      const selectedResource = state.resources.find(r => r.id === formData.resource_id);
      
      console.log('💾 PLAN IMPLEMENTATION - Saving assignment with resource lookup:', {
        formDataResourceId: formData.resource_id,
        availableResourceIds: state.resources.map(r => r.id),
        selectedResource: selectedResource ? {
          id: selectedResource.id,
          name: selectedResource.name,
          email: selectedResource.email,
          role_type: selectedResource.role_type
        } : null
      });

      if (!selectedResource) {
        console.error('❌ RESOURCE NOT FOUND - Available resources:', state.resources.map(r => ({ id: r.id, name: r.name })));
        toast.error('Selected resource not found');
        return;
      }

      const assignmentData = {
        project_id: selectedProject.id,
        resource_id: formData.resource_id, // This should be the UUID from resources table
        task_id: formData.task_id,
        start_date: formData.start_date,
        end_date: formData.end_date,
        allocated_percentage: formData.allocated_percentage,
        rate: (formData.rate !== undefined && formData.rate !== '' ? Number(formData.rate) : (selectedResource.rate ?? null)),
        allocation_hrs: formData.allocation_hrs,
        duration: formData.duration,
        user_id: user.id,
        utilization_percentage: formData.allocated_percentage,
        name: selectedResource.name,
        role: formData.role || selectedResource.role,
        role_type: formData.role_type || selectedResource.role_type,
        email: selectedResource.email // This is the critical fix - ensuring email is saved from the resource
      };

      console.log('💾 FINAL ASSIGNMENT DATA WITH EMAIL:', assignmentData);

      if (state.editingAssignment) {
        if (state.userRole === 'Manager' || state.userRole === 'Director') {
          const { data, error } = await supabase.functions.invoke(
            'manage-manager-resource-assignment',
            { body: { action: 'update', id: state.editingAssignment.id, payload: assignmentData } }
          );
          if (error) throw error;
          if ((data as any)?.error) throw new Error((data as any).error);
        } else {
          const { error } = await supabase
            .from('resource_assignment')
            .update(assignmentData)
            .eq('id', state.editingAssignment.id);
          if (error) throw error;
        }
        toast.success('Resource assignment updated successfully');
      } else {
        if (state.userRole === 'Manager' || state.userRole === 'Director') {
          const { data, error } = await supabase.functions.invoke(
            'manage-manager-resource-assignment',
            { body: { action: 'insert', payload: assignmentData } }
          );
          if (error) throw error;
          if ((data as any)?.error) throw new Error((data as any).error);
        } else {
          const { error } = await supabase
            .from('resource_assignment')
            .insert(assignmentData);
          if (error) throw error;
        }
        toast.success('Resource assignment added successfully');
      }

      updateState({ isDialogOpen: false });
      
      // Clear cache for the affected task and immediately fetch fresh data
      if (formData.task_id) {
        const updatedCache = { ...state.assignmentCache };
        delete updatedCache[formData.task_id];
        updateState({ assignmentCache: updatedCache });
        
        // Immediately fetch fresh assignments to update UI
        await fetchTaskAssignments(formData.task_id, true);
        
        // Update project_tasks resources field after save
        await updateProjectTaskResources(formData.task_id, formData.task_id);
        
        console.log('🔄 PLAN FIX: UI refreshed with updated assignments after save');
      }
    } catch (error) {
      console.error('Error saving resource assignment:', error);
      toast.error('Failed to save resource assignment');
    }
  };

  const refreshTaskAssignments = () => {
    if (state.selectedTaskId) {
      fetchTaskAssignments(state.selectedTaskId, true);
    }
  };

  // Get current task assignments from cache or empty array
  const getCurrentTaskAssignments = (): ResourceAssignment[] => {
    if (!state.selectedTaskId) return [];
    return state.assignmentCache[state.selectedTaskId]?.assignments || [];
  };

  // Get unique task IDs from project tasks
  const getUniqueTaskIds = (): string[] => {
    return state.projectTasks.map(task => task.taskId || task.name).filter(Boolean);
  };

  // Auto-fetch assignments when task is selected
  useEffect(() => {
    if (state.selectedTaskId && selectedProject) {
      fetchTaskAssignments(state.selectedTaskId);
    }
  }, [state.selectedTaskId, selectedProject]);

  return {
    assignments: getCurrentTaskAssignments(),
    resources: state.resources,
    projectTasks: state.projectTasks,
    selectedTaskId: state.selectedTaskId,
    setSelectedTaskId: (taskId: string | null) => updateState({ selectedTaskId: taskId }),
    uniqueTaskIds: getUniqueTaskIds(),
    isLoading: state.isLoading,
    isDialogOpen: state.isDialogOpen,
    setIsDialogOpen: (open: boolean) => updateState({ isDialogOpen: open }),
    editingAssignment: state.editingAssignment,
    setEditingAssignment: (assignment: ResourceAssignment | null) => updateState({ editingAssignment: assignment }),
    isOrgChartOpen: state.isOrgChartOpen,
    setIsOrgChartOpen: (open: boolean) => updateState({ isOrgChartOpen: open }),
    handleDeleteResource,
    handleSaveResource,
    fetchResourceAssignments: refreshTaskAssignments,
    fetchTaskAssignments,
    userRole: state.userRole,
    canAddResource: state.canAddResource,
  };
};
